import classnames from "classnames";
import React, {
    Fragment,
    ReactNode,
    Ref,
    SyntheticEvent,
    useEffect,
    useRef,
} from "react";
import { useInView } from "react-intersection-observer";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { isReviewsEnabled } from "@utils/configuration/aem";
import { applyConditionalClasses } from "@utils/domElements";
import { ConditionalWrapper } from "@utils/helpers";

import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";
import {
    trackProductClickImpressionFromJson,
    trackProductImpressionFromJson,
} from "@services/tracking";
import { ProductTileToProductImpressionModel } from "@services/tracking/mappers/productTile.mapper";

import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";

import { ProductImageContainer } from "@components/Product/ProductImage/productImage.container";
import Brand from "@components/Product/ProductTile/components/brand/brand.component";
import ProductTileButton from "@components/Product/ProductTile/components/button/productTileButton.container";
import CategoryLink from "@components/Product/ProductTile/components/categoryLink/categoryLink.component";
import Comparison from "@components/Product/ProductTile/components/comparison/comparison.component";
import { ProductTileDescription } from "@components/Product/ProductTile/components/description/description.component";
import Price from "@components/Product/ProductTile/components/price/price.container";
import { PromotionLabel } from "@components/Product/ProductTile/components/promotionLabel/promotionLabel.component";
import { PromotionRibbon } from "@components/Product/ProductTile/components/promotionRibbon/promotionRibbon.component";
import ProductTitle from "@components/Product/ProductTile/components/title/productTitle.component";
import {
    ProductTileChildComponent,
    ProductTileConfig,
} from "@components/Product/ProductTile/productTile.config";
import { WishlistButtonContainer } from "@components/Wishlist/WishlistButton/WishlistButton.container";

import { DeliveryTypes } from "./components/deliveryTypes/deliveryTypes.container";
import { ProductTileReviews } from "./components/reviews/reviews.component";
import "./productTile.scss";

const PRODUCT_TILE_BREAKPOINT = 135;

export type ProductTileComponentProps = {
    product: ProductTileModel;
    lazyLoadImage?: boolean;
    allowInstantAddToCart?: boolean;
    extraButtons?: () => ReactNode;
    cart?: CartModel | null;
    extraOnClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
    analyticsContext?: {
        placement: number;
        list: string;
    };
    children?: ReactNode;
    className?: string;
    productTileConfig: ProductTileConfig;
    onClickTracking?: () => void;
    ref?: Ref<HTMLDivElement | HTMLAnchorElement>;
};

const ProductTileComponent = ({
    ref,
    product,
    allowInstantAddToCart,
    extraButtons,
    cart,
    extraOnClick,
    analyticsContext,
    lazyLoadImage,
    children,
    className,
    productTileConfig: {
        amountOfFeatures,
        border,
        tilePadding,
        hoverEffect,
        imageRenditions,
        components,
        columnFractions,
    },
    onClickTracking,
}: ProductTileComponentProps) => {
    const productTileRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const setSizeClass = () => {
            if (!productTileRef.current) return;

            const width = productTileRef.current.clientWidth;

            applyConditionalClasses(
                () => width >= PRODUCT_TILE_BREAKPOINT,
                productTileRef.current,
            )({
                ifTrue: "productTile--l",
                ifFalse: "productTile--s",
            });
        };

        setSizeClass();
        window.addEventListener("resize", setSizeClass);

        return () => window.removeEventListener("resize", setSizeClass);
    }, [productTileRef.current]);

    const onProductTileClick = () => {
        const productImpression = ProductTileToProductImpressionModel(product);
        onClickTracking && onClickTracking();
        trackProductClickImpressionFromJson(
            productImpression,
            analyticsContext?.list || "product-tile-components",
            analyticsContext?.placement,
        );
    };

    const trackProductTile = (inView: boolean) => {
        if (inView) {
            const productImpression =
                ProductTileToProductImpressionModel(product);
            trackProductImpressionFromJson(
                productImpression,
                analyticsContext?.list || "product-tile-components",
                analyticsContext?.placement,
            );
        }
    };

    const { ref: invViewRef } = useInView({
        triggerOnce: true,
        delay: 100,
        onChange: trackProductTile,
        trackVisibility: true,
    });

    const productTileComponents = {
        [ProductTileChildComponent.PRODUCT_IMAGE]: () => (
            <ProductImageContainer
                lazyLoad={lazyLoadImage}
                title={product.title}
                isEcoChequesAccepted={product.ecochecksAcceptedAsPayment}
                medias={product.medias}
                renditions={imageRenditions}
            />
        ),
        [ProductTileChildComponent.PRODUCT_PRICE]: () => (
            <Price
                className={"productTile__price"}
                showStrippedPrice={!!product.strikethroughPrice}
                showConvertedPriceAsMainPrice={
                    product.showConvertedPriceAsMainPrice
                }
                conversionUnit={product.conversionUnit}
                conversionPrice={product.conversionPrice}
                strikethroughPrice={product.strikethroughPrice}
                currentConversionPrice={product.currentConversionPrice}
                salesPrice={product.salesPrice}
            />
        ),
        [ProductTileChildComponent.DELIVERY_TYPES]: () => (
            <DeliveryTypes
                productVisibilityType={product.productVisibilityType}
            />
        ),
        [ProductTileChildComponent.CATEGORY_LINK]: () => (
            <CategoryLink
                className={"productTile__categoryLink small"}
                superCategoryUrl={product.superCategoryUrl}
                superCategoryName={product.superCategoryName}
            />
        ),
        [ProductTileChildComponent.PROMOTION_LABEL]: () => (
            <PromotionLabel
                className={"productTile__promotionLabel"}
                promoLabel={product.promoLabel}
            />
        ),
        [ProductTileChildComponent.PROMOTION_RIBBON]: () => (
            <PromotionRibbon
                className={"productTile__promotionRibbon"}
                promoLabel={product.promoLabel}
            />
        ),
        [ProductTileChildComponent.TITLE]: () => (
            <ProductTitle
                className={"productTile__title"}
                title={product.title}
            />
        ),
        [ProductTileChildComponent.BUTTON]: () => (
            <ProductTileButton
                className={classnames(components.length > 1 && "flex-grow-1")}
                product={product}
                allowInstantAddToCart={!!allowInstantAddToCart}
                extraOnClick={extraOnClick}
                cart={cart}
            />
        ),
        [ProductTileChildComponent.FEATURES]: () => (
            <ProductTileDescription
                className={"productTile__features"}
                items={product.features}
                showItems={true}
                amountOfFeatures={amountOfFeatures}
            />
        ),
        [ProductTileChildComponent.REVIEWS]: () =>
            isReviewsEnabled() ? (
                <ProductTileReviews sku={product.sku} />
            ) : null,
        [ProductTileChildComponent.WISHLIST_BUTTON]: () => (
            <WishlistButtonContainer
                sku={product.sku}
                withReload={true}
                trackingProduct={{
                    product: {
                        ean: product.ean,
                        productTitle: product.title,
                        sku: product.sku,
                        analytics: product.analytics,
                        brandName: product.brand?.name,
                        currentPrice: product.salesPrice,
                        roundedDiscountPercentage: product.discountPercentage,
                    },
                }}
            />
        ),
        [ProductTileChildComponent.COMPARISON]: () =>
            FeatureToggles.isEnabled(FeatureToggleKeys.PRODUCT_COMPARISON) && (
                <Comparison sku={product.sku} />
            ),
        [ProductTileChildComponent.BRAND]: () => (
            <Brand brandName={product.brand?.name} />
        ),
        [ProductTileChildComponent.EXTRA_BUTTONS]: extraButtons,
    };

    const tileGridTemplateColumns = Object.assign(
        new Array(components.length).fill(1),
        columnFractions,
    )
        .slice(0, components.length)
        .map(fraction => `${fraction}fr`)
        .join(" ");

    return (
        <div
            className={classnames(
                "productTile h-100 d-flex flex-column position-relative",
                className,
                border && "productTile--border card--all",
                tilePadding && "productTile--padding",
                product.disablePDPLink && "productTile--noLink",
                hoverEffect && "productTile--hoverEnabled",
                components.length > 1
                    ? "productTile--horizontal"
                    : "productTile--vertical",
            )}
            ref={node => {
                invViewRef(node);
                productTileRef.current = node;
            }}
        >
            <ConditionalWrapper
                condition={product.disablePDPLink}
                wrapper={children => (
                    <div
                        className={
                            "text-decoration-none text-reset flex-grow-1 d-flex flex-column"
                        }
                        ref={node => {
                            if (!ref) return;
                            if (typeof ref === "function") ref(node);
                            else ref.current = node;
                        }}
                    >
                        {children}
                    </div>
                )}
                alternative={children => (
                    <a
                        href={product.url}
                        ref={node => {
                            if (!ref) return;
                            if (typeof ref === "function") ref(node);
                            else ref.current = node;
                        }}
                        onClick={onProductTileClick}
                        className={
                            "text-decoration-none text-reset flex-grow-1 d-flex flex-column"
                        }
                    >
                        {children}
                    </a>
                )}
            >
                <>
                    {children}
                    <div
                        className={"productTile__cols flex-grow-1 w-100"}
                        style={{
                            gridTemplateColumns: tileGridTemplateColumns,
                        }}
                    >
                        {components.map(col => (
                            <div
                                key={col.join("-")}
                                className={
                                    "productTile__rows flex-grow-1 d-flex flex-column"
                                }
                            >
                                {col.map(row => (
                                    <div
                                        key={row.join("-")}
                                        className={classnames(
                                            `productTile__row d-flex justify-content-between w-100`,
                                            row
                                                .map(
                                                    c =>
                                                        `productTile__row--${c}`,
                                                )
                                                .join(" "),
                                        )}
                                    >
                                        {row.map(component => (
                                            <Fragment key={component}>
                                                {productTileComponents[
                                                    component
                                                ]?.()}
                                            </Fragment>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            </ConditionalWrapper>
        </div>
    );
};

export default ProductTileComponent;
